import { useState } from "react";
import {
  CopyButton,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type ColligativeStrings,
  CUSTOM,
  colligativeMessages,
  DEFAULT_SIG,
  TEMPERATURE_OPTIONS,
  VantHoffFields,
} from "@/components/tools/chem-physical/colligative-shared";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  OSMOTIC_CONCENTRATION_UNITS,
  OSMOTIC_VOLUME_UNITS,
  type OsmoticSolution,
  type OsmoticTerm,
  solveOsmotic,
} from "@/lib/tool-kit/chem-physical/colligative";
import {
  HEAT_MASS_UNITS,
  unitOptionsOf,
  unitSymbol,
} from "@/lib/tool-kit/chem-physical/heat";
import {
  PRESSURE_UNITS,
  temperatureSymbol,
} from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

const PRESSURE_OPTIONS = PRESSURE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const CONCENTRATION_OPTIONS = unitOptionsOf(OSMOTIC_CONCENTRATION_UNITS);
const VOLUME_OPTIONS = unitOptionsOf(OSMOTIC_VOLUME_UNITS);
const MASS_OPTIONS = unitOptionsOf(HEAT_MASS_UNITS);
const R_TEXT = "8.314462618";
const R_LITRE_ATM = 0.082_057_366_08;

interface OsmoticUnits {
  pi: string;
  m: string;
  t: string;
  mass: string;
  vol: string;
}

function pressureText(
  fmt: ReturnType<typeof useToolNumber>,
  pascals: number,
  unit: string,
  sig: number
): string {
  const factor = PRESSURE_UNITS.find((u) => u.id === unit)?.pascals ?? 1;
  const symbol = PRESSURE_UNITS.find((u) => u.id === unit)?.symbol ?? "Pa";
  return `${formatSig(fmt, pascals / factor, sig)} ${symbol}`;
}

function OsmoticAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: OsmoticSolution;
  texts: Record<string, string>;
  units: OsmoticUnits;
  sig: number;
  strings: ColligativeStrings;
}) {
  const fmt = useToolNumber();
  const p = (value: number) => formatPlain(fmt, value);
  const { unknown } = solution;
  const labels = {
    pi: strings.osmoticPressure,
    m: strings.osmoticConcentration,
    mm: strings.soluteMolarMass,
  };
  const answerText = {
    pi: pressureText(fmt, solution.pi, units.pi, sig),
    m: `${formatSig(fmt, solution.answer, sig)} ${unitSymbol(OSMOTIC_CONCENTRATION_UNITS, units.m)}`,
    mm: `${formatSig(fmt, solution.answer, sig)} g/mol`,
  }[unknown];
  const cubic = solution.m * 1000;
  const conversions: string[] = [];
  if (units.t !== "K") {
    conversions.push(
      t(strings.convertLine, {
        label: strings.termT,
        value: texts.t.trim(),
        unit: temperatureSymbol(units.t),
        converted: `${p(solution.kelvin)} K`,
      })
    );
  }
  if (unknown !== "m" && unknown !== "mm") {
    conversions.push(
      t(strings.concentrationLine, { molar: p(solution.m), cubic: p(cubic) })
    );
  }
  const equation = {
    pi: strings.osmoticRearrangedPi,
    m: strings.osmoticRearrangedM,
    mm: strings.osmoticRearrangedMm,
  }[unknown];
  const substituted = {
    pi: t(strings.osmoticSubstitutedPi, {
      i: p(solution.i),
      c: p(cubic),
      r: R_TEXT,
      t: p(solution.kelvin),
      pi: p(solution.pi),
    }),
    m: t(strings.osmoticSubstitutedM, {
      pi: p(solution.pi),
      i: p(solution.i),
      r: R_TEXT,
      t: p(solution.kelvin),
      c: p(cubic),
      molar: p(solution.m),
    }),
    mm: t(strings.osmoticSubstitutedMm, {
      pi: p(solution.pi),
      i: p(solution.i),
      r: R_TEXT,
      t: p(solution.kelvin),
      molar: p(solution.m),
    }),
  }[unknown];
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        {unknown === "pi" ? (
          <>
            <StatCard
              label="atm"
              value={formatSig(fmt, solution.pi / 101_325, sig)}
            />
            <StatCard
              label="kPa"
              value={formatSig(fmt, solution.pi / 1000, sig)}
            />
          </>
        ) : (
          <StatCard
            label={strings.osmoticPressure}
            value={pressureText(fmt, solution.pi, units.pi, sig)}
          />
        )}
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.osmoticEquation}</p>
        {unknown === "pi" ? null : <p>{equation}</p>}
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
        {unknown === "mm" &&
        solution.molarMass !== null &&
        solution.moles !== null &&
        solution.massGrams !== null &&
        solution.volumeLitres !== null ? (
          <>
            <p>
              {t(strings.osmoticMolesLine, {
                molar: p(solution.m),
                vol: p(solution.volumeLitres),
                moles: p(solution.moles),
              })}
            </p>
            <p>
              {t(strings.osmoticMolarMassLine, {
                mass: p(solution.massGrams),
                moles: p(solution.moles),
                result: formatSig(fmt, solution.molarMass, sig + 1),
              })}
            </p>
          </>
        ) : null}
        <p>
          {t(strings.rLine, { r: R_TEXT, rAtm: formatPlain(fmt, R_LITRE_ATM) })}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}

export function OsmoticPanel({
  strings,
  sigChoice,
}: {
  strings: ColligativeStrings;
  sigChoice: string;
}) {
  const [unknown, setUnknown] = useState<OsmoticTerm>("pi");
  const [choice, setChoice] = useState("sucrose:ideal");
  const [text, setText] = useState({
    pi: "",
    m: "0.30",
    t: "37",
    i: "1",
    mass: "",
    vol: "",
  });
  const [unit, setUnit] = useState<OsmoticUnits>({
    pi: "atm",
    m: "M",
    t: "C",
    mass: "g",
    vol: "L",
  });
  const outcome = solveOsmotic({ unknown, text, unit });
  const set = (patch: Partial<typeof text>) => setText({ ...text, ...patch });
  const typed = {
    pi: [text.m, text.t],
    m: [text.pi, text.t],
    mm: [text.pi, text.t, text.mass, text.vol],
  }[unknown];
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<string, string> = {
    pi: strings.osmoticPressure,
    m: strings.osmoticConcentration,
    t: strings.termT,
    i: strings.vantHoff,
    mass: strings.soluteMass,
    vol: strings.solutionVolume,
  };
  const label = (key: "pi" | "m", base: string) =>
    unknown === key ? t(strings.answerSuffix, { label: base }) : base;
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="col-os-solve"
        onChange={setUnknown}
        options={[
          { value: "pi", label: strings.osmoticPressure },
          { value: "m", label: strings.osmoticConcentration },
          { value: "mm", label: strings.soluteMolarMass },
        ]}
        value={unknown}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {unknown === "pi" ? (
          <QuantityInput
            id="col-os-pi"
            label={label("pi", labels.pi)}
            onUnitChange={(value) => setUnit({ ...unit, pi: value })}
            onValueChange={() => undefined}
            solved
            unit={unit.pi}
            unitLabel={strings.unitPressure}
            units={PRESSURE_OPTIONS}
            value=""
          />
        ) : (
          <QuantityInput
            id="col-os-pi"
            label={labels.pi}
            onUnitChange={(value) => setUnit({ ...unit, pi: value })}
            onValueChange={(pi) => set({ pi })}
            unit={unit.pi}
            unitLabel={strings.unitPressure}
            units={PRESSURE_OPTIONS}
            value={text.pi}
          />
        )}
        {unknown === "mm" ? null : (
          <QuantityInput
            id="col-os-m"
            label={label("m", labels.m)}
            onUnitChange={(value) => setUnit({ ...unit, m: value })}
            onValueChange={(m) => set({ m })}
            solved={unknown === "m"}
            unit={unit.m}
            unitLabel={strings.unitConcentration}
            units={CONCENTRATION_OPTIONS}
            value={text.m}
          />
        )}
        <QuantityInput
          id="col-os-t"
          label={labels.t}
          onUnitChange={(value) => setUnit({ ...unit, t: value })}
          onValueChange={(value) => set({ t: value })}
          unit={unit.t}
          unitLabel={strings.unitTemperature}
          units={TEMPERATURE_OPTIONS}
          value={text.t}
        />
        <VantHoffFields
          choice={choice}
          idPrefix="col-os"
          onChoice={(next, factor) => {
            setChoice(next);
            if (factor !== null) {
              set({ i: factor });
            }
          }}
          onValue={(i) => {
            setChoice(CUSTOM);
            set({ i });
          }}
          strings={strings}
          value={text.i}
        />
        {unknown === "mm" ? (
          <>
            <QuantityInput
              id="col-os-mass"
              label={labels.mass}
              onUnitChange={(value) => setUnit({ ...unit, mass: value })}
              onValueChange={(mass) => set({ mass })}
              unit={unit.mass}
              unitLabel={strings.unitMass}
              units={MASS_OPTIONS}
              value={text.mass}
            />
            <QuantityInput
              id="col-os-vol"
              label={labels.vol}
              onUnitChange={(value) => setUnit({ ...unit, vol: value })}
              onValueChange={(vol) => set({ vol })}
              unit={unit.vol}
              unitLabel={strings.unitVolume}
              units={VOLUME_OPTIONS}
              value={text.vol}
            />
          </>
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <OsmoticAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={colligativeMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
